import {
  Decimal,
  type CreditProductConfig,
  type DecisionPolicy,
  type DelinquencyPolicy,
  type AgentSettings,
  type FundingConfig,
  type LimitRule,
  type OriginationPolicy,
  type UsuryRate,
} from '@paguz/contracts';
import type { z } from 'zod';
import { SETTINGS, type ReversalSettings, type SettingKey } from '../../api/admin-schemas.ts';
import { formatRate } from '../../lib/format.ts';

/**
 * Validación de configuración en la consola: primero el esquema de `@paguz/contracts`, luego las
 * reglas de negocio que el esquema no expresa. El servidor valida lo mismo (fuente de verdad);
 * aquí se adelanta para que el operador vea el error junto al campo.
 */
export type FieldErrors = Record<string, string>;

export function schemaErrors(schema: z.ZodType, value: unknown): FieldErrors {
  const result = schema.safeParse(value);
  if (result.success) return {};
  const errors: FieldErrors = {};
  for (const issue of result.error.issues) {
    const path = issue.path.map(String).join('.');
    errors[path] ??= issue.message;
  }
  return errors;
}

/** Tasa de usura vigente de la modalidad en una fecha de negocio (`YYYY-MM-DD`). */
export function currentUsuryRate(
  rates: readonly UsuryRate[],
  modality: UsuryRate['modality'],
  businessDate: string,
): UsuryRate | null {
  return (
    rates.find(
      (r) => r.modality === modality && r.validFrom <= businessDate && r.validTo >= businessDate,
    ) ?? null
  );
}

export function productErrors(product: CreditProductConfig, usury: UsuryRate | null): FieldErrors {
  const errors: FieldErrors = {};
  if (!usury) {
    errors.rateEA = 'No hay tasa de usura vigente para la modalidad: no se puede guardar.';
    return errors;
  }
  const max = new Decimal(usury.rateEA);
  if (new Decimal(product.rateEA).gt(max)) {
    errors.rateEA = `Supera la tasa de usura vigente (${formatRate(usury.rateEA)}).`;
  }
  if (product.moratoriumRateEA !== 'USURY' && new Decimal(product.moratoriumRateEA).gt(max)) {
    errors.moratoriumRateEA = `Supera la tasa de usura vigente (${formatRate(usury.rateEA)}).`;
  }
  if (new Decimal(product.limitMin.amount).gt(product.limitMax.amount)) {
    errors.limitMax = 'El cupo máximo debe ser mayor o igual al mínimo.';
  }
  if (
    product.purchaseMax &&
    new Decimal(product.purchaseMin.amount).gt(product.purchaseMax.amount)
  ) {
    errors.purchaseMax = 'La compra máxima debe ser mayor o igual a la mínima.';
  }
  if (product.allowedInstallments.length === 0) {
    errors.allowedInstallments = 'Elige al menos un número de cuotas.';
  }
  if (product.mipymeFee.cap && new Decimal(product.mipymeFee.rate).gt(product.mipymeFee.cap)) {
    errors['mipymeFee.rate'] = 'La comisión supera el tope legal configurado.';
  }
  return errors;
}

export function decisionPolicyErrors(
  policy: DecisionPolicy,
  product?: CreditProductConfig,
): FieldErrors {
  const errors: FieldErrors = {};
  const max = new Decimal(policy.autoApproval.maxLimit.amount);
  if (policy.autoApproval.enabled && max.lte(0)) {
    errors['autoApproval.maxLimit'] = 'Define un cupo máximo autoaprobable mayor que cero.';
  }
  if (product && max.gt(product.limitMax.amount)) {
    errors['autoApproval.maxLimit'] = 'No puede superar el cupo máximo del producto.';
  }
  if (policy.autoRejection.enabled && policy.autoRejection.knockOuts.length === 0) {
    errors['autoRejection.knockOuts'] = 'Con el autorrechazo activo, elige al menos un knock-out.';
  }
  return errors;
}

export function reversalErrors(settings: ReversalSettings): FieldErrors {
  const errors: FieldErrors = {};
  const { maxDays, decisionDueBusinessDays } = settings.disputePolicy;
  if (maxDays < decisionDueBusinessDays) {
    errors['disputePolicy.maxDays'] =
      'La disputa debe poder durar al menos el plazo para decidir (días hábiles).';
  }
  return errors;
}

export function delinquencyErrors(policy: DelinquencyPolicy): FieldErrors {
  const errors: FieldErrors = {};
  const bands = policy.ratingBands;
  if (bands[0]?.minDpd !== 0) errors.ratingBands = 'El primer tramo debe empezar en 0 días.';
  for (let i = 0; i < bands.length; i++) {
    const band = bands[i];
    const next = bands[i + 1];
    if (!band) continue;
    if (band.maxDpd !== null && band.maxDpd < band.minDpd) {
      errors[`ratingBands.${i}.maxDpd`] = 'El máximo debe ser mayor o igual al mínimo.';
    }
    if (next && (band.maxDpd === null || next.minDpd !== band.maxDpd + 1)) {
      errors[`ratingBands.${i + 1}.minDpd`] =
        `Debe empezar en ${band.maxDpd === null ? '—' : band.maxDpd + 1} (tramos continuos).`;
    }
    if (!next && band.maxDpd !== null) {
      errors[`ratingBands.${i}.maxDpd`] = 'El último tramo debe quedar abierto (sin máximo).';
    }
  }
  if (policy.bureauReporting.enabled && policy.bureauReporting.priorNoticeDays < 20) {
    errors['bureauReporting.priorNoticeDays'] =
      'La Ley 1266 exige avisar al menos 20 días antes del reporte.';
  }
  return errors;
}

export function fundingErrors(config: FundingConfig): FieldErrors {
  const errors: FieldErrors = {};
  if (config.mode === 'SETTLEMENT_ACCOUNT' && !config.settlementAccount) {
    errors.mode = 'El modo cuenta puente requiere una cuenta puente configurada.';
  }
  for (const mode of ['SETTLEMENT_ACCOUNT', 'LINKED_ACCOUNT_JIT'] as const) {
    if (new Decimal(config.gmf[mode].rate).gt('0.01')) {
      errors[`gmf.${mode}.rate`] = 'Revisa la tasa: el GMF vigente es 4 × 1.000 (0,4 %).';
    }
  }
  return errors;
}

export interface ValidationContext {
  usury?: UsuryRate | null;
  product?: CreditProductConfig;
}

/** Errores de esquema + negocio para una sección de configuración. */
export function validateSetting(key: SettingKey, value: unknown, ctx: ValidationContext = {}) {
  const errors = schemaErrors(SETTINGS[key], value);
  if (Object.keys(errors).length > 0) return errors;
  switch (key) {
    case 'product':
      return ctx.usury === undefined ? {} : productErrors(value as CreditProductConfig, ctx.usury);
    case 'decisionPolicy':
      return decisionPolicyErrors(value as DecisionPolicy, ctx.product);
    case 'reversal':
      return reversalErrors(value as ReversalSettings);
    case 'delinquencyPolicy':
      return delinquencyErrors(value as DelinquencyPolicy);
    case 'funding':
      return fundingErrors(value as FundingConfig);
    case 'originationPolicy':
      return originationErrors(value as OriginationPolicy);
    case 'limits':
      return limitsErrors((value as { rules: LimitRule[] }).rules);
    case 'ai':
      return aiErrors(value as AgentSettings);
  }
}

/** Límites M8: mínimo < máximo por compra ≤ diario ≤ mensual; una regla activa por tipo y alcance. */
export function limitsErrors(rules: readonly LimitRule[]): FieldErrors {
  const errors: FieldErrors = {};
  const amountOf = (type: string) => {
    const rule = rules.find((r) => r.active && r.scope === 'TENANT' && r.params.ruleType === type);
    return rule && 'amount' in rule.params ? new Decimal(rule.params.amount.amount) : null;
  };
  const indexOf = (type: string) =>
    rules.findIndex((r) => r.active && r.scope === 'TENANT' && r.params.ruleType === type);
  const min = amountOf('MIN_PER_TX');
  const max = amountOf('MAX_PER_TX');
  const daily = amountOf('MAX_DAILY_AMOUNT');
  const monthly = amountOf('MAX_MONTHLY_AMOUNT');
  if (min && max && min.gte(max))
    errors[`rules.${indexOf('MIN_PER_TX')}.params.amount`] =
      'El mínimo por compra debe ser menor que el máximo.';
  if (max && daily && max.gt(daily))
    errors[`rules.${indexOf('MAX_DAILY_AMOUNT')}.params.amount`] =
      'El máximo diario no puede ser menor que el máximo por compra.';
  if (daily && monthly && daily.gt(monthly))
    errors[`rules.${indexOf('MAX_MONTHLY_AMOUNT')}.params.amount`] =
      'El máximo mensual no puede ser menor que el diario.';
  const seen = new Set<string>();
  rules.forEach((r, i) => {
    const key = `${r.scope}:${r.scopeRef ?? ''}:${r.params.ruleType}`;
    if (r.active && seen.has(key))
      errors[`rules.${i}.active`] = 'Ya hay una regla activa de este tipo.';
    if (r.active) seen.add(key);
  });
  return errors;
}

/** Originación: pesos del scorecard suman 100 %, edades coherentes. */
export function originationErrors(policy: OriginationPolicy): FieldErrors {
  const errors: FieldErrors = {};
  if (policy.age.min >= policy.age.max)
    errors['age.max'] = 'La edad máxima debe ser mayor que la mínima.';
  const total = policy.scorecard.reduce((acc, f) => acc.plus(f.weight), new Decimal(0));
  if (!total.eq(1))
    errors['scorecard.0.weight'] =
      `Los pesos del scorecard deben sumar 100 % (hoy ${formatRate(total.toFixed(4), '', 0)}).`;
  if (policy.knockOuts.length === 0)
    errors.knockOuts = 'Deja al menos un knock-out (p. ej. listas restrictivas).';
  return errors;
}

/** IA: presupuestos con un límite (USD o tokens). */
export function aiErrors(settings: AgentSettings): FieldErrors {
  const errors: FieldErrors = {};
  settings.budgets.forEach((b, i) => {
    if (b.limitUsd === null && b.limitTokens === null)
      errors[`budgets.${i}.limitUsd`] = 'Indica un límite en USD o en tokens.';
    if (b.limitUsd !== null && !/^\d+(\.\d{1,2})?$/.test(b.limitUsd))
      errors[`budgets.${i}.limitUsd`] = 'Monto en USD con hasta 2 decimales.';
  });
  return errors;
}
